<template v-cloak>

	<!-- Header -->
	<Head :title="formData.title"/>

	<!-- Form Landing Page Design -->
	<div v-if="! isProcessing">

		<!-- If not set content on landing page -->
		<template v-if="! formData.form_header_page_template">

			<!-- Temporary hide, later need to use for Facebook OG -->
			<div class="d-none">
				<img v-if="formData.thumbnail" class="card-img-top mb-5" :src="formData.thumbnail">

				<h4 class="mb-3">
					{{ formData.title }}
				</h4>

				<p class="mb-3 text-14" v-html="formData.descriptions"></p>
			</div>
		</template>

		<!-- If set content on landing page -->
		<template v-else>
			<!-- Sandboxed iframe isolates merchant HTML/CSS/JS from the form -->
			<iframe
				v-if="formData.form_header_page_template_editor === 'html'"
				:srcdoc="htmlHeaderSrcdoc"
				class="w-100 d-block border-0"
				scrolling="no"
				@load="resizeHtmlFrame"
			></iframe>
			<div v-else class="container col-5 pt-4 mobile-fix-container">
				<div id="custom-template-wrapper" class="bg-white border landing-page-form-padding rounded mobile-fix-rounded" v-html="formData.form_header_page_template"></div>
			</div>
		</template>
	</div>

	<!-- Donation Form -->
	<main>
		<div class="container col-5 mobile-fix-container">
			<div id="landing-page-form" class="mt-4 mb-4 mobile-fix-rounded">
				<template v-if="step === 1">

					<!-- Step 1: Campaign -->
					<campaigns-section :campaigns="campaigns" :selected-campaigns="selectedCampaigns" @add-campaign="addCampaign"/>

					<!-- Step 2: Package -->
					<template v-if="selectedCampaigns.length">
						<template v-for="(campaign, idx) in selectedCampaigns" :key="idx">
							<packages-section :campaign="campaign" :packages="campaignPackages(campaign)" :donations="donations" @donate="donate" @removeDonation="removeDonation"/>
						</template>
					</template>

					<!-- Step 3: Total -->
					<total-section v-if="total > 0" :selected-campaigns="selectedCampaigns" :form-data="formData" :donations="donations" @next="next"/>
				</template>

				<!-- Step 4: Payment -->
				<payment-section v-else
		             :formData="formData"
		             :total="total"
		             :summary="summary"
		             :city-opts="cityOpts"
		             :demo_mode="formData.demo_mode"
		             :tenant="tenant"
		             :state-opts="stateOpts"
		             :country-opts="countryOpts"
		             :bank-accounts="bankAccounts"
		             :fpx-banks-list="fpxBanksList"
		             :fpx-banks-logo="fpxBanksLogo"
		             :payment-gateways="paymentGateways"
		             @prev="prev"
		        />
			</div>

			<!-- FOMO Options -->
			<template v-if="props.formData.scroll_to_form_btn_enabled">
				<img v-if="props.formData.scroll_to_form_btn_image_enabled" :src="props.formData.scroll_to_form_btn_image_url" id="scroll-to-form-btn">
				<a v-else class="btn btn-primary btn-lg text-white" id="scroll-to-form-btn">
					{{ props.formData.scroll_to_form_btn_text }}
				</a>
			</template>

			<!-- Footer -->
			<footer-section :tenant="tenant"/>
		</div>
	</main>
</template>

<!-- Scripts -->
<script setup>
import CampaignsSection from "@/Pages/Donations/ModernForm/CampaignsSection";
import PackagesSection from "@/Pages/Donations/ModernForm/PackagesSection";
import TotalSection from "@/Pages/Donations/ModernForm/TotalSection";
import PaymentSection from "@/Pages/Donations/ModernForm/PaymentSection";
import FooterSection from "@/Components/FooterSection.vue";
import {Head} from "@inertiajs/vue3";
import {computed, onBeforeMount, onMounted, ref, inject} from "vue";
import useHelpers from "@/Composables/UseHelpers";
import useTrackingCode from "@/Composables/UseTrackingCode";
import useLandingPageFrame from "@/Composables/UseLandingPageFrame";

const { $t } = useHelpers();

const { sendFacebookPixelViewContentEvent, sendFacebookPixelAddToCartEvent, sendGoogleAdsAddToCartEvent} = useTrackingCode();

const props = defineProps(['tenant', 'formData', 'bankAccounts', 'campaigns', 'cityOpts', 'stateOpts', 'countryOpts', 'paymentGateways', 'latestTransactions', 'fpxBanksList', 'fpxBanksLogo']);

Lang.setLocale(props.formData.language ?? 'en');

const isProcessing = ref(false);

const routeHelper = route();
const step = ref(1);
const selectedCampaigns = ref([]);
const donations = ref([]);
const toastr = inject('toastr');

const { htmlHeaderSrcdoc, resizeHtmlFrame } = useLandingPageFrame(props.formData);

onBeforeMount(() => {
	props.campaigns.forEach((campaign) => {
		campaign.packages.forEach((cpackage) => {
			donations.value.push(cpackage);
		});
	});
});

// FOMO Options
onMounted(() => {
	donations.value.forEach(function(donation) {
		if ( ! donation.custom_amount_enabled && donation.count > 0 && selectedCampaigns.value.indexOf(donation.campaign) < 0) {
			selectedCampaigns.value.push(donation.campaign);
		}
	});

	if (props.formData.mandate_enabled && props.formData.mandate_application_type === '03' || (! props.formData.mandate_enabled && selectedCampaigns.value.length > 0 && (total.value) > 0)) {
		step.value = 2;
	}

	if (props.formData.social_proof_enabled) {
		props.latestTransactions.forEach((donation, idx) => {
			setTimeout(function() {
				toastr.success(`${donation.name} telah menyumbang sebanyak ${donation.amount} pada ${donation.date}...`, {
					position: 'top-right',
					duration: 4000
				});
			}, 2500 * ((idx+1) * 3));
		});
	}

	// Scroll button
	let scrollButtons = document.querySelectorAll('a[href="#landing-page-form"], #scroll-to-form-btn');

	for (let i = 0; i < scrollButtons.length; i++) {
		scrollButtons.item(i).addEventListener('click', function handleClick(event) {
			event.preventDefault();
			document.querySelector('#landing-page-form').scrollIntoView({
				behavior: 'smooth'
			});
		});
	}

	$(window).scroll(function() {
		var formTopOffset = getCoords(document.getElementById('landing-page-form')).top;

		if ($(this).scrollTop() > (formTopOffset - 80)) {
			$('#scroll-to-form-btn').hide();
		} else {
			$('#scroll-to-form-btn').show();
		}
	});

});

const summary = computed(() => {
	return donations.value.reduce(function (r, a) {
		r[a.campaign] = r[a.campaign] || [];
		r[a.campaign].push(a);

		return r;
	}, Object.create(null));
});

const total = computed(() => {
	return donations.value.map(donation => donation.amount * donation.count).reduce((prev, next) => prev + next, 0.00)
});

// Cross browser version
function getCoords(elem) {
	let box = elem.getBoundingClientRect(),
	body = document.body,
	docEl = document.documentElement,
	scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop,
	scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft,
	clientTop = docEl.clientTop || body.clientTop || 0,
	clientLeft = docEl.clientLeft || body.clientLeft || 0,
	top  = box.top +  scrollTop - clientTop,
	left = box.left + scrollLeft - clientLeft;

	return { top: Math.round(top), left: Math.round(left) };
}

function addCampaign(campaign) {
	if (selectedCampaigns.value.indexOf(campaign) < 0) {
		selectedCampaigns.value.push(campaign);

		// Meta (Facebook) Pixel (Standard Events)
		if (props.formData.facebook_tracking.facebook_pixel_id) {
            sendFacebookPixelViewContentEvent(campaign);
		}
	} else {
		let idx = selectedCampaigns.value.indexOf(campaign);
		selectedCampaigns.value.splice(idx, 1);
		donations.value = donations.value.filter(donation => donation.campaign !== campaign);
	}
}

function campaignPackages(campaignName) {
	let campaign = props.campaigns.find(campaign => campaign.name === campaignName);

	return campaign.packages;
}

function donate(cpackage) {
	let selectedPackage = {...cpackage};

	if (selectedPackage.custom_amount_enabled) {
		let amount = prompt($t('donation-form.enter_custom_amount'));
		if (amount < selectedPackage.min_custom_amount) return alert($t('donation-form.minimum_amount') + selectedPackage.min_custom_amount);
		selectedPackage.amount = parseFloat(amount);
	}

	if (!selectedPackage.custom_amount_enabled && donations.value.filter(donation => donation.code === selectedPackage.code && donation.campaign === selectedPackage.campaign).length) {
		donations.value.map((donation) => {
			if (donation.code === selectedPackage.code && donation.campaign === selectedPackage.campaign && cpackage.count <= 99) {
				donation.count += 1
			}
		});
	} else {
    selectedPackage.count = 1;
		donations.value.push(selectedPackage);
	}

	// Meta (Facebook) Pixel (Standard Events)
	if (props.formData.facebook_tracking.facebook_pixel_id) {
        sendFacebookPixelAddToCartEvent(selectedPackage, props.formData, props.formData.module);
	}

	// Google Ads Tracking
	if (props.formData.google_ads_tracking.google_ads_id && props.formData.google_ads_tracking.add_to_cart_conversion_label) {
		sendGoogleAdsAddToCartEvent(selectedPackage, props.formData, props.formData.module);
	}
}

// Remove donation package
function removeDonation(donationToRemove) {
	let donation = donations.value.find(donation => donation.code === donationToRemove.code && donation.campaign === donationToRemove.campaign);
	donation.count--;

	if (donation.count === 0) {
		let idx = donations.value.indexOf(donation);
		donations.value.splice(idx, 1);
	}
}

function next() {
	step.value++;
}

function prev() {
	step.value--
}
</script>

<!-- Styles -->
<style>
[v-cloak] {
	display: none;
}
</style>
